import React from 'react';
import { Helmet } from 'react-helmet-async';

const SEOHead = ({ 
  title, 
  description, 
  keywords, 
  canonicalUrl, 
  ogImage, 
  ogType = 'website',
  lang = 'en',
  structuredData 
}) => {
  const defaultTitle = 'Logistiat - Fleet Management Software Saudi Arabia';
  const defaultDescription = 'Complete fleet management system for Saudi Arabia. Shipment tracking, trip costing, maintenance, and driver management with Arabic/English support.';
  const defaultKeywords = 'ادارة اسطول، نظام ادارة اسطول، ادارة الاسطول السعودية، برنامج ادارة اسطول، نظام ادارة الاسطول، fleet management, Saudi Arabia, logistics software, shipment tracking, trip costing, fuel management, driver management, TMS, transportation management system, KSA logistics';
  const defaultImage = 'https://logistiat.app/images/dashboard-screenshot.png';
  
  const siteUrl = 'https://logistiat.app';
  const fullTitle = title ? `${title} | Logistiat` : defaultTitle;
  const fullCanonicalUrl = canonicalUrl ? `${siteUrl}${canonicalUrl}` : siteUrl;

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <html lang={lang} dir={lang === 'ar' ? 'rtl' : 'ltr'} />
      <title>{fullTitle}</title>
      <meta name="description" content={description || defaultDescription} />
      <meta name="keywords" content={keywords || defaultKeywords} />
      <meta name="author" content="Logistiat" />
      <meta name="robots" content="index, follow" />
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      
      {/* Canonical URL */}
      <link rel="canonical" href={fullCanonicalUrl} />
      
      {/* Open Graph Meta Tags */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description || defaultDescription} />
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={fullCanonicalUrl} />
      <meta property="og:image" content={ogImage || defaultImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content="Logistiat" />
      <meta property="og:locale" content={lang === 'ar' ? 'ar_SA' : 'en_US'} />
      
      {/* Twitter Card Meta Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description || defaultDescription} />
      <meta name="twitter:image" content={ogImage || defaultImage} />
      <meta name="twitter:site" content="@logistiat" />
      
      {/* Additional SEO Meta Tags */}
      <meta name="theme-color" content="#2563eb" />
      <meta name="msapplication-TileColor" content="#2563eb" />
      
      {/* Geo Meta Tags for Saudi Arabia */}
      <meta name="geo.region" content="SA" />
      <meta name="geo.country" content="Saudi Arabia" />
      <meta name="geo.placename" content="Riyadh" />
      
      {/* Language Alternates */}
      <link rel="alternate" hrefLang="en" href={`${siteUrl}/en`} />
      <link rel="alternate" hrefLang="ar" href={siteUrl} />
      <link rel="alternate" hrefLang="x-default" href={siteUrl} />
      
      {/* Additional SEO Meta Tags */}
      <meta name="application-name" content="Logistiat" />
      <meta name="apple-mobile-web-app-title" content="Logistiat" />
      <meta name="format-detection" content="telephone=no" />
      <meta name="mobile-web-app-capable" content="yes" />
      <meta name="msapplication-config" content="/browserconfig.xml" />
      <meta name="msapplication-tap-highlight" content="no" />
      
      {/* Preconnect to external domains */}
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
      
      {/* DNS Prefetch */}
      <link rel="dns-prefetch" href="https://formspree.io" />
      
      {/* Structured Data */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
      
      {/* Default Organization Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          "name": "Logistiat",
          "url": "https://logistiat.app",
          "logo": "https://logistiat.app/images/dashboard-screenshot.png",
          "description": "نظام ادارة اسطول متكامل للمملكة العربية السعودية - Fleet management software for Saudi Arabia trucking companies",
          "address": {
            "@type": "PostalAddress",
            "addressCountry": "SA",
            "addressRegion": "Riyadh Province",
            "addressLocality": "Riyadh"
          },
          "contactPoint": {
            "@type": "ContactPoint",
            "telephone": "+966-54-428-8686",
            "contactType": "customer service",
            "availableLanguage": ["English", "Arabic"]
          },
          "sameAs": [
            "https://twitter.com/logistiat",
            "https://linkedin.com/company/logistiat"
          ]
        })}
      </script>
    </Helmet>
  );
};

export default SEOHead;